/**
 * WindParticleOverlay
 * Factory that creates a Google Maps OverlayView subclass for animated wind particles.
 * The class must be created at runtime (not import time) because google.maps
 * is loaded asynchronously.
 */

import type { WindGrid } from '@/services/windData.service'
import { getWindColor } from '@/utils/windColorScale'

interface Particle {
  x: number       // longitude
  y: number       // latitude
  prevX: number   // previous longitude (for trail line)
  prevY: number   // previous latitude
  age: number     // current age in frames
  maxAge: number  // lifetime in frames
  speed: number   // last interpolated wind speed (for coloring)
}

interface AffineTransform {
  scaleX: number
  scaleY: number
  offsetX: number
  offsetY: number
  swLng: number
  neLng: number
  neLat: number
  swLat: number
}

// Speed bracket boundaries for batch rendering
const SPEED_BRACKETS = [5, 10, 15, 25, 40, Infinity]

function getParticleCount(viewportArea: number, zoom: number): number {
  // Base density from viewport size (~1 particle per 1200 px²)
  const base = Math.round(viewportArea / 1200)

  // Very aggressive reduction at higher zoom
  let zoomFactor: number
  if (zoom <= 5) zoomFactor = 1.0
  else if (zoom <= 7) zoomFactor = 0.5
  else if (zoom <= 9) zoomFactor = 0.15
  else if (zoom <= 11) zoomFactor = 0.06
  else zoomFactor = 0.03

  return Math.max(30, Math.min(1800, Math.round(base * zoomFactor)))
}

function getTrailScale(zoom: number): number {
  if (zoom <= 5) return 0.5
  if (zoom <= 7) return 0.7
  if (zoom <= 9) return 0.85
  if (zoom <= 11) return 1.0
  return 1.1
}

// Trail fade — lower = trails disappear faster
function getTrailFade(zoom: number): number {
  if (zoom <= 6) return 0.92
  if (zoom <= 8) return 0.88
  if (zoom <= 10) return 0.82
  if (zoom <= 12) return 0.75
  return 0.65
}

// Max particle age — shorter at high zoom so they recycle faster
function getMaxAge(zoom: number): number {
  if (zoom <= 6) return 120
  if (zoom <= 8) return 80
  if (zoom <= 10) return 50
  return 30
}

export interface IWindParticleOverlay extends google.maps.OverlayView {
  setWindGrid(grid: WindGrid): void
  setOpacity(opacity: number): void
  startAnimation(): void
  stopAnimation(): void
}

let OverlayClass: (new () => IWindParticleOverlay) | null = null

/**
 * Creates (or returns cached) the WindParticleOverlay class.
 * Must be called after Google Maps JS API is loaded.
 */
export function createWindParticleOverlay(): IWindParticleOverlay {
  if (!OverlayClass) {
    OverlayClass = class extends google.maps.OverlayView implements IWindParticleOverlay {
      private canvas: HTMLCanvasElement | null = null
      private ctx: CanvasRenderingContext2D | null = null
      private particles: Particle[] = []
      private windGrid: WindGrid | null = null
      private animFrameId: number | null = null
      private isAnimating = false
      private opacity = 0.5
      private transform: AffineTransform | null = null
      private currentZoom = 7
      private viewportArea = 0
      private frameCount = 0

      onAdd(): void {
        this.canvas = document.createElement('canvas')
        this.canvas.style.position = 'absolute'
        this.canvas.style.top = '0'
        this.canvas.style.left = '0'
        this.canvas.style.pointerEvents = 'none'

        const panes = this.getPanes()
        panes?.overlayLayer.appendChild(this.canvas)

        this.ctx = this.canvas.getContext('2d')
      }

      draw(): void {
        if (!this.canvas) return

        const projection = this.getProjection()
        if (!projection) return

        const map = this.getMap() as google.maps.Map | null
        if (!map) return

        const bounds = map.getBounds()
        if (!bounds) return

        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()

        const nePixel = projection.fromLatLngToDivPixel(ne)
        const swPixel = projection.fromLatLngToDivPixel(sw)
        if (!nePixel || !swPixel) return

        const width = Math.abs(nePixel.x - swPixel.x)
        const height = Math.abs(swPixel.y - nePixel.y)

        const dpr = window.devicePixelRatio || 1

        // Position and size the canvas (CSS size in map pixels, backing store scaled by DPR)
        this.canvas.style.left = `${swPixel.x}px`
        this.canvas.style.top = `${nePixel.y}px`
        this.canvas.style.width = `${width}px`
        this.canvas.style.height = `${height}px`
        this.canvas.width = width * dpr
        this.canvas.height = height * dpr

        if (this.ctx) {
          this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        }

        // Precompute affine transform for lat/lng -> canvas pixel (in CSS pixels)
        this.transform = {
          scaleX: width / (ne.lng() - sw.lng()),
          scaleY: height / (sw.lat() - ne.lat()),
          offsetX: 0,
          offsetY: 0,
          swLng: sw.lng(),
          neLng: ne.lng(),
          neLat: ne.lat(),
          swLat: sw.lat(),
        }

        this.currentZoom = map.getZoom() ?? 7

        // Use map container size (not overlay canvas) for particle density
        const div = map.getDiv()
        this.viewportArea = div ? div.clientWidth * div.clientHeight : width * height

        // Resize particle pool if needed
        this.resizeParticlePool()
      }

      onRemove(): void {
        this.stopAnimation()
        if (this.canvas?.parentNode) {
          this.canvas.parentNode.removeChild(this.canvas)
        }
        this.canvas = null
        this.ctx = null
        this.particles = []
      }

      setWindGrid(grid: WindGrid): void {
        this.windGrid = grid
      }

      setOpacity(opacity: number): void {
        this.opacity = Math.max(0, Math.min(1, opacity))
      }

      startAnimation(): void {
        if (this.isAnimating) return
        this.isAnimating = true
        this.resizeParticlePool()
        this.animate()
      }

      stopAnimation(): void {
        this.isAnimating = false
        if (this.animFrameId !== null) {
          cancelAnimationFrame(this.animFrameId)
          this.animFrameId = null
        }
        if (this.ctx && this.canvas) {
          this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        }
      }

      private resizeParticlePool(): void {
        const targetCount = getParticleCount(this.viewportArea || 1920 * 1080, this.currentZoom)
        const current = this.particles.length

        if (current < targetCount) {
          for (let i = current; i < targetCount; i++) {
            this.particles.push(this.createRandomParticle())
          }
        } else if (current > targetCount) {
          this.particles.length = targetCount
        }
      }

      private createRandomParticle(): Particle {
        const t = this.transform
        const lng = t ? t.swLng + Math.random() * (t.neLng - t.swLng) : -98 + Math.random() * 50
        const lat = t ? t.neLat + Math.random() * (t.swLat - t.neLat) : 25 + Math.random() * 25
        return {
          x: lng,
          y: lat,
          prevX: lng,
          prevY: lat,
          age: Math.floor(Math.random() * getMaxAge(this.currentZoom)),
          maxAge: Math.floor(getMaxAge(this.currentZoom) * (0.5 + Math.random() * 0.5)),
          speed: 0,
        }
      }

      private animate = (): void => {
        if (!this.isAnimating) return

        this.step()
        this.render()

        this.animFrameId = requestAnimationFrame(this.animate)
      }

      private step(): void {
        if (!this.windGrid || !this.transform) return

        const grid = this.windGrid
        const trailScale = getTrailScale(this.currentZoom)
        // Compensate for zoom: higher zoom = more pixels per degree,
        // so reduce geographic displacement to keep visual speed reasonable
        const zoomCompensation = Math.pow(1.5, Math.max(0, this.currentZoom - 5))
        const speedScale = (0.002 * trailScale) / zoomCompensation

        for (const p of this.particles) {
          p.prevX = p.x
          p.prevY = p.y

          const { u, v, speed } = this.interpolateWind(p.x, p.y, grid)
          p.speed = speed

          p.x += u * speedScale
          p.y += v * speedScale
          p.age++

          if (p.age >= p.maxAge || !this.isInBounds(p.x, p.y)) {
            this.resetParticle(p)
          }
        }
      }

      private interpolateWind(
        lng: number,
        lat: number,
        grid: WindGrid
      ): { u: number; v: number; speed: number } {
        const { lats, lons, uGrid, vGrid, speedGrid } = grid
        const numLons = lons.length

        const latIdx = this.findBracketIndex(lats, lat)
        const lonIdx = this.findBracketIndex(lons, lng)

        if (latIdx < 0 || lonIdx < 0) return { u: 0, v: 0, speed: 0 }

        const latFrac = (lat - lats[latIdx]) / (lats[latIdx + 1] - lats[latIdx])
        const lonFrac = (lng - lons[lonIdx]) / (lons[lonIdx + 1] - lons[lonIdx])

        const i00 = latIdx * numLons + lonIdx
        const i10 = (latIdx + 1) * numLons + lonIdx
        const i01 = latIdx * numLons + (lonIdx + 1)
        const i11 = (latIdx + 1) * numLons + (lonIdx + 1)

        const u =
          uGrid[i00] * (1 - latFrac) * (1 - lonFrac) +
          uGrid[i10] * latFrac * (1 - lonFrac) +
          uGrid[i01] * (1 - latFrac) * lonFrac +
          uGrid[i11] * latFrac * lonFrac

        const v =
          vGrid[i00] * (1 - latFrac) * (1 - lonFrac) +
          vGrid[i10] * latFrac * (1 - lonFrac) +
          vGrid[i01] * (1 - latFrac) * lonFrac +
          vGrid[i11] * latFrac * lonFrac

        const speed =
          speedGrid[i00] * (1 - latFrac) * (1 - lonFrac) +
          speedGrid[i10] * latFrac * (1 - lonFrac) +
          speedGrid[i01] * (1 - latFrac) * lonFrac +
          speedGrid[i11] * latFrac * lonFrac

        return { u, v, speed }
      }

      private isInBounds(lng: number, lat: number): boolean {
        if (!this.transform) return false
        const t = this.transform
        return lng >= t.swLng && lng <= t.neLng && lat >= t.swLat && lat <= t.neLat
      }

      private resetParticle(p: Particle): void {
        const t = this.transform
        p.x = t ? t.swLng + Math.random() * (t.neLng - t.swLng) : p.x
        p.y = t ? t.neLat + Math.random() * (t.swLat - t.neLat) : p.y
        p.prevX = p.x
        p.prevY = p.y
        p.age = 0
        p.maxAge = Math.floor(getMaxAge(this.currentZoom) * (0.5 + Math.random() * 0.5))
        p.speed = 0
      }

      /** Binary search to find index i such that arr[i] <= value <= arr[i+1]. Returns -1 if outside range. */
      private findBracketIndex(arr: number[], value: number): number {
        const n = arr.length
        if (n < 2 || value < arr[0] || value > arr[n - 1]) return -1

        let low = 0
        let high = n - 2

        while (low <= high) {
          const mid = (low + high) >> 1
          if (value < arr[mid]) {
            high = mid - 1
          } else if (value > arr[mid + 1]) {
            low = mid + 1
          } else {
            return mid
          }
        }

        return -1
      }

      private render(): void {
        const ctx = this.ctx
        const canvas = this.canvas
        const t = this.transform
        if (!ctx || !canvas || !t) return

        // Trail effect: fade previous frame instead of clearing
        const fade = getTrailFade(this.currentZoom)
        ctx.globalCompositeOperation = 'destination-in'
        ctx.fillStyle = `rgba(0, 0, 0, ${fade})`
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.globalCompositeOperation = 'source-over'

        // Periodically scrub near-transparent ghost pixels that accumulate
        // from floating-point imprecision in the fade.
        // Avoid expensive getImageData/putImageData on large canvases.
        this.frameCount++
        const w = canvas.width
        const h = canvas.height
        if (w > 0 && h > 0) {
          const area = w * h
          const isSmallCanvas = area <= 480000 // ~800x600
          const scrubInterval = isSmallCanvas ? 180 : 600

          if (this.frameCount >= scrubInterval) {
            this.frameCount = 0
            if (isSmallCanvas) {
              const imageData = ctx.getImageData(0, 0, w, h)
              const data = imageData.data
              for (let i = 3; i < data.length; i += 4) {
                if (data[i] < 8) data[i] = 0
              }
              ctx.putImageData(imageData, 0, 0)
            } else {
              ctx.clearRect(0, 0, w, h)
            }
          }
        }

        ctx.globalAlpha = this.opacity

        // Batch particles by speed bracket for fewer strokeStyle changes
        for (let bracket = 0; bracket < SPEED_BRACKETS.length; bracket++) {
          const maxSpeed = SPEED_BRACKETS[bracket]
          const minSpeed = bracket > 0 ? SPEED_BRACKETS[bracket - 1] : 0
          const midSpeed = (minSpeed + Math.min(maxSpeed, 60)) / 2
          const color = getWindColor(midSpeed)

          ctx.beginPath()
          ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`
          ctx.lineWidth = midSpeed > 25 ? 1.5 : 1

          for (const p of this.particles) {
            if (p.speed < minSpeed || p.speed >= maxSpeed) continue
            if (p.age === 0) continue

            const fadeIn = Math.min(1, p.age / 10)
            const fadeOut = Math.min(1, (p.maxAge - p.age) / 10)
            if (fadeIn * fadeOut < 0.05) continue

            const x1 = (p.prevX - t.swLng) * t.scaleX
            const y1 = (p.prevY - t.neLat) * t.scaleY
            const x2 = (p.x - t.swLng) * t.scaleX
            const y2 = (p.y - t.neLat) * t.scaleY

            ctx.moveTo(x1, y1)
            ctx.lineTo(x2, y2)
          }

          ctx.stroke()
        }

        ctx.globalAlpha = 1
      }
    }
  }

  return new OverlayClass()
}
